import type { RouteRecordRaw } from "vue-router";
import type { MenuListItem } from "@/services/oaOffice/role";

/**
 * 从 menuList 构建权限查找表
 * 用于快速查询某个 rule_value 是否在权限列表中
 *
 * @param menuList 菜单列表
 * @returns 权限查找表 { rule_value: icon }
 */
const buildPermissionMap = (menuList: MenuListItem[]): Map<string, string> => {
	const permissionMap = new Map<string, string>();

	const traverse = (items: MenuListItem[]) => {
		for (const item of items) {
			permissionMap.set(item.rule_value, item.icon);

			if (item.children && item.children.length > 0) {
				traverse(item.children);
			}
		}
	};

	traverse(menuList);

	return permissionMap;
};

/**
 * 从路由获取权限值
 * 优先从 meta.permission 获取，再从顶层 permission 字段获取
 *
 * @param route 路由配置
 * @returns 权限值，如果不存在则返回 undefined
 */
const getRoutePermission = (route: RouteRecordRaw): string | undefined => {
	const metaPermission = route.meta?.permission as string | undefined;

	if (metaPermission) {
		return metaPermission;
	}

	const routePermission = (route as unknown as Record<string, unknown>)?.permission as
		| string
		| undefined;

	return routePermission;
};

/**
 * 检查路由是否有权限
 * 如果路由未配置 permission，则默认有权限
 *
 * @param route 路由配置
 * @param permissionMap 权限查找表
 * @returns 是否有权限
 */
const hasRoutePermission = (route: RouteRecordRaw, permissionMap: Map<string, string>): boolean => {
	const permission = getRoutePermission(route);

	// 没有 permission 字段，默认有权限
	if (!permission) {
		return true;
	}

	return permissionMap.has(permission);
};

/**
 * 获取权限对应的 icon
 *
 * @param permission 权限值
 * @param permissionMap 权限查找表
 * @returns icon 值
 */
const getPermissionIcon = (
	permission: string,
	permissionMap: Map<string, string>
): string | undefined => {
	return permissionMap.get(permission);
};

/**
 * 递归过滤路由，只保留有权限的路由
 * 同时将权限对应的 icon 同步到 meta 中
 * 对于有子路由的路由，如果所有子路由都被过滤掉，则该路由也会被过滤掉
 * 如果只有部分子路由保留，则 redirect 会指向第一个保留的子路由
 *
 * @param routes 路由列表
 * @param permissionMap 权限查找表
 * @returns 过滤后的路由列表
 */
const filterRoutesByPermission = (
	routes: RouteRecordRaw[],
	permissionMap: Map<string, string>
): RouteRecordRaw[] => {
	return routes
		.filter((route) => hasRoutePermission(route, permissionMap))
		.map((route) => {
			const newRoute = { ...route };
			const permission = getRoutePermission(route);

			// 同步 icon 到 meta
			if (permission) {
				const icon = getPermissionIcon(permission, permissionMap);

				if (icon && newRoute.meta) {
					newRoute.meta = {
						...newRoute.meta,
						icon,
					};
				}
			}

			// 递归处理子路由
			if (route.children && route.children.length > 0) {
				const filteredChildren = filterRoutesByPermission(route.children, permissionMap);

				// 如果所有子路由都被过滤掉，则该路由也应该被过滤掉
				if (filteredChildren.length === 0) {
					return null;
				}

				// 如果有子路由被过滤掉，需要更新 redirect 指向第一个保留的子路由
				if (filteredChildren.length < route.children.length) {
					const firstChild = filteredChildren[0];

					if (firstChild && newRoute.meta) {
						newRoute.redirect = firstChild.path;
					}
				}

				newRoute.children = filteredChildren;
			}

			return newRoute;
		})
		.filter((route): route is RouteRecordRaw => route !== null);
};

/**
 * 使用权限数据动态生成路由
 *
 * @param appRoutes 应用路由列表
 * @param menuList 菜单列表（权限数据）
 * @returns 经过权限过滤的路由列表
 */
export const generateDynamicRoutes = (
	appRoutes: RouteRecordRaw[],
	menuList: MenuListItem[]
): RouteRecordRaw[] => {
	const permissionMap = buildPermissionMap(menuList);

	return filterRoutesByPermission(appRoutes, permissionMap);
};
